/** @typedef {import('swiper').Swiper} Swiper */

export const LOOP_ADDITIONAL_SLIDES_MAX = 3;
export const DEFAULT_SLIDE_GAP_PX = 20;

/**
 * @returns {boolean}
 */
export function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Resolve a CSS length custom property to px.
 * @param {HTMLElement} parent
 * @param {string} cssVar
 * @param {number} [fallback]
 */
export function readCssLengthPx(
    parent,
    cssVar,
    fallback = DEFAULT_SLIDE_GAP_PX,
) {
    const probe = document.createElement('div');
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.width = cssVar;
    parent.appendChild(probe);
    const probed = probe.getBoundingClientRect().width;
    probe.remove();
    return Number.isFinite(probed) && probed > 0 ?
        Math.round(probed) :
        fallback;
}

/**
 * Swiper requires slides.length >= slidesPerView + slidesPerGroup + loopAdditionalSlides.
 * @param {number} domCount
 * @param {number} perView
 * @param {number} [max]
 */
export function getLoopAdditionalSlides(
    domCount,
    perView,
    max = LOOP_ADDITIONAL_SLIDES_MAX,
) {
    return Math.max(
        0,
        Math.min(max, Math.floor(domCount - perView - 1)),
    );
}

/**
 * @param {Swiper} swiper
 * @returns {number}
 */
export function getSlidesPerView(swiper) {
    const perView = swiper.params.slidesPerView;
    return typeof perView === 'number' ? perView : 1;
}

/**
 * Toggle autoplay when a class is added/removed on a root element.
 * @param {HTMLElement} rootEl
 * @param {Swiper} swiper
 * @param {string} disabledClass
 */
export function bindAutoplayClassToggle(rootEl, swiper, disabledClass) {
    if (!rootEl || !swiper?.autoplay || !disabledClass) return;

    const syncAutoplay = () => {
        if (rootEl.classList.contains(disabledClass)) {
            swiper.autoplay.stop();
        } else {
            swiper.autoplay.start();
        }
    };

    syncAutoplay();
    const observer = new MutationObserver(syncAutoplay);
    observer.observe(rootEl, {
        attributes: true,
        attributeFilter: ['class'],
    });
}
